// cmd.exe — a Windows XP command prompt that actually does things: a fake C: drive you can walk,
// network commands that answer in-character, live chain reads (PAYDAY, PRICE), START <app> to open
// any window on the desktop, a JS REPL, and PYTHON — a real CPython 3.12 (Pyodide, WebAssembly)
// loaded from jsDelivr only when someone types it. Community ask 2026-09-08: "virtual terminal…
// maybe python installed too or java". Python: yes. Java: no. (real)
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LIVE as CASINO_LIVE, PRACTICE as CASINO_PRACTICE } from 'lib/casino';
import styled from 'styled-components';

import { README } from '../Notepad';
import { GAMES, findGame, openGame } from '../Games/catalog';
import { isUnlocked } from '../Basement/state';
import { getLedger } from 'data/ledger';
import { compute, fmtAmt } from 'data/epochs';
import { fetchMsft, fmtUsd, fmtCompact } from 'data/msft';

const CA = '0x88888cC1c17FfcBa563F418c61596b3fa2Ca2e2c';
const DISTRIBUTOR = '0xF346218BFC30564efe39988e6a14188525297d98';
const HOME = 'C:\\Documents and Settings\\fren';
const PYODIDE_BASE = 'https://cdn.jsdelivr.net/pyodide/v0.27.2/full/';
const STAMP = '09/05/2026  07:59 PM';
const sleep = ms => new Promise(r => setTimeout(r, ms));
const commas = n => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

// ---- the C: drive (keys lowercase; dirs keep display casing) ----
const DESKTOP_FILES = [
  ['The Internet.lnk', 1], ['Time Machine.lnk', 1], ['DIVIDENDS.exe', 88888], ['BUY $INTERNET.exe', 8888],
  ['MISSION.exe', 55000000], ['MSFT.exe', 419], ['PAYDAY.exe', 1700], ['VERIFY.exe', 3],
  ['Network Neighborhood.lnk', 1], ['README.txt', README.length], ['My Computer.lnk', 1], ['My Videos.lnk', 1],
  ['Windows Media Player.lnk', 1], ['3D Pinball.lnk', 1], ['Games.lnk', 1], ['Minesweeper.exe', 119808],
  ['Winamp.lnk', 1], ['Paint.exe', 340992], ['Command Prompt.lnk', 1], ['DO NOT OPEN.exe', 666],
  ['Recycle Bin.lnk', 1], ['Windows Messenger.lnk', 1],
];
const FS = {
  'c:\\': { dirs: ['Documents and Settings', 'Program Files', 'WINDOWS'], files: [['ca.txt', 128], ['AUTOEXEC.BAT', 0], ['CONFIG.SYS', 0], ['boot.ini', 211], ['pagefile.sys', 805306368]] },
  'c:\\documents and settings': { dirs: ['fren', 'All Users'], files: [] },
  'c:\\documents and settings\\all users': { dirs: ['Desktop', 'Shared Documents'], files: [] },
  'c:\\documents and settings\\all users\\desktop': { dirs: [], files: [['everyone.lnk', 1]] },
  'c:\\documents and settings\\all users\\shared documents': { dirs: [], files: [['the_internet.txt', 5500000000]] },
  'c:\\documents and settings\\fren': { dirs: ['Desktop', 'Favorites', 'My Documents'], hidden: ['.internet'], files: [['README.txt', README.length], ['NTUSER.DAT', 262144]] },
  // ARG level 1: the folder that shouldn't exist. `dir /a` shows it; the nfo names the door.
  'c:\\documents and settings\\fren\\.internet': { dirs: [], files: [['basement.nfo', 888], ['do_not_read.txt', 1]] },
  'c:\\documents and settings\\fren\\desktop': { dirs: [], files: DESKTOP_FILES },
  'c:\\documents and settings\\fren\\favorites': { dirs: [], files: [['internetcoin.vip.url', 88], ['google 2003.url', 88], ['hampsterdance.url', 88]] },
  'c:\\documents and settings\\fren\\my documents': { dirs: ['My Music', 'My Pictures', 'My Videos'], files: [['pay_stub.png', 88888]] },
  'c:\\documents and settings\\fren\\my documents\\my music': { dirs: [], files: [['llama.mp3', 1990000]] },
  'c:\\documents and settings\\fren\\my documents\\my pictures': { dirs: [], files: [['bliss_lime.jpg', 4194304]] },
  'c:\\documents and settings\\fren\\my documents\\my videos': { dirs: [], files: [['(67 clips, 302 MB — open My Videos on the desktop)', 0]] },
  'c:\\program files': { dirs: ['Games', 'Internet Explorer', 'Messenger', 'Winamp', 'Windows Media Player'], files: [] },
  'c:\\program files\\games': { dirs: [], files: GAMES.map(g => [`${g.id}.exe`, 1990 + g.year]) },
  'c:\\program files\\internet explorer': { dirs: [], files: [['iexplore.exe', 93184], ['timemachine.dll', 2003]] },
  'c:\\program files\\messenger': { dirs: [], files: [['msmsgs.exe', 1694208], ['nudge.wav', 88888]] },
  'c:\\program files\\winamp': { dirs: [], files: [['winamp.exe', 1024000], ['llama.wav', 1000]] },
  'c:\\program files\\windows media player': { dirs: [], files: [['wmplayer.exe', 512000], ['old_movies.m3u', 32]] },
  'c:\\windows': { dirs: ['Fonts', 'Media', 'system32'], files: [['explorer.exe', 1032192], ['notepad.exe', 69120], ['win.ini', 512], ['Bliss.bmp', 1440054]] },
  'c:\\windows\\fonts': { dirs: [], files: [['tahoma.ttf', 681216], ['trebuc.ttf', 133556], ['lucon.ttf', 63088]] },
  'c:\\windows\\media': { dirs: [], files: [['Windows XP Startup.wav', 424406], ['ding.wav', 77874], ['youve_got_dividends.wav', 88888]] },
  'c:\\windows\\system32': { dirs: [], files: [['cmd.exe', 389120], ['ping.exe', 17408], ['tracert.exe', 12288], ['ipconfig.exe', 57344], ['payday.exe', 8888], ['internet.dll', 1000000000], ['hal.dll', 134400], ['python312.dll', 0]] },
};

function resolve(cwd, arg) {
  const a = String(arg || '').trim().replace(/\//g, '\\').replace(/^"|"$/g, '');
  if (!a || a === '.') return cwd;
  let parts;
  if (/^[a-z]:/i.test(a)) {
    if (!/^c:/i.test(a)) return null;
    parts = a.replace(/^[a-z]:\\?/i, '').split('\\');
  } else if (a.startsWith('\\')) parts = a.slice(1).split('\\');
  else parts = cwd.replace(/^C:\\?/, '').split('\\').filter(Boolean).concat(a.split('\\'));
  const out = [];
  for (const p of parts) {
    if (!p || p === '.') continue;
    if (p === '..') out.pop();
    else out.push(p);
  }
  let path = 'C:\\';
  for (const p of out) {
    const node = FS[path.toLowerCase()];
    if (!node) return null;
    const hit = [...node.dirs, ...(node.hidden || [])].find(d => d.toLowerCase() === p.toLowerCase());
    if (!hit) return null;
    path = path === 'C:\\' ? `C:\\${hit}` : `${path}\\${hit}`;
  }
  return path;
}
function listing(path, all) {
  const node = FS[path.toLowerCase()];
  const rows = [' Volume in drive C is THE INTERNET', ' Volume Serial Number is 8888-2E2C', '', ` Directory of ${path}`, ''];
  if (path !== 'C:\\') rows.push(`${STAMP}    <DIR>          .`, `${STAMP}    <DIR>          ..`);
  node.dirs.forEach(d => rows.push(`${STAMP}    <DIR>          ${d}`));
  const hidden = all ? node.hidden || [] : [];
  hidden.forEach(d => rows.push(`${STAMP}    <DIR>          ${d}`));
  let bytes = 0;
  node.files.forEach(([n, b]) => { bytes += b; rows.push(`${STAMP} ${commas(b).padStart(17)} ${n}`); });
  rows.push(`${String(node.files.length).padStart(16)} File(s) ${commas(bytes).padStart(14)} bytes`);
  rows.push(`${String(node.dirs.length + hidden.length).padStart(16)} Dir(s)  888,888,888,888 bytes free`);
  return rows.join('\n');
}
const BASEMENT_NFO = `
  ░▒▓█ the basement █▓▒░           basement.nfo · 888 bytes · 2025

  you found the folder. good. most people never type dir /a.

  there is a door on the internal web. not the real internet —
  the one inside Internet Explorer on this desktop. type this in
  the address bar:

      http://basement.internetcoin.vip

  the door wants a key. the key has been in front of you the whole
  time: count the eights that open the contract address. that many
  eights. nothing else. type them at the door.

  don't tell the others. let them find the folder.

                                            — the internet (real)
`;
function tree(path = 'C:\\', prefix = '', out = []) {
  const node = FS[path.toLowerCase()];
  if (!node) return out;
  node.dirs.forEach((d, i) => {
    const last = i === node.dirs.length - 1;
    out.push(`${prefix}${last ? '└───' : '├───'}${d}`);
    tree(path === 'C:\\' ? `C:\\${d}` : `${path}\\${d}`, `${prefix}${last ? '    ' : '│   '}`, out);
  });
  return out;
}

const COLORS = { 0: '#000', 1: '#000080', 2: '#008000', 3: '#008080', 4: '#800000', 5: '#800080', 6: '#808000', 7: '#c0c0c0', 8: '#808080', 9: '#0000ff', a: '#ccff00', b: '#00ffff', c: '#ff0000', d: '#ff00ff', e: '#ffff00', f: '#ffffff' };

const START_MAP = {
  msn: 'Windows Messenger', messenger: 'Windows Messenger', chat: 'Windows Messenger',
  pinball: 'Pinball', games: 'Games', movies: 'Windows Media Player', wmp: 'Windows Media Player', wmplayer: 'Windows Media Player', mediaplayer: 'Windows Media Player',
  paint: 'Paint', mspaint: 'Paint', notepad: 'Notepad', readme: 'Notepad', winamp: 'Winamp', mine: 'Minesweeper', minesweeper: 'Minesweeper',
  payday: 'Payday', msft: 'Msft', verify: 'Verify', mission: 'Mission', dividends: 'Dividends', buy: 'Buy', videos: 'My Videos', myvideos: 'My Videos',
  ie: 'Internet Explorer', iexplore: 'Internet Explorer', internet: 'Internet Explorer', timemachine: 'Time Machine', wayback: 'Time Machine',
  network: 'Network', mycomputer: 'My Computer', computer: 'My Computer', recycle: 'Recycle Bin', cmd: 'Command Prompt',
};

const HELP = `For more information on a specific command, ask in Windows Messenger.
CA        prints the one and only contract address
CD        changes the current directory (CD .. / CD \\ / CD Desktop)
CLS       clears the screen
COLOR     sets console colours, e.g. COLOR 0A
DATE      displays the date
DIR       lists files and folders
ECHO      displays a message
EXIT      quits cmd.exe
HELP      this
HOSTNAME  prints the host name
IPCONFIG  network configuration
JS        JavaScript REPL (type exit to leave)
NETSTAT   active connections
PAYDAY    live payroll status, read from the chain
PING      sends 4 echo requests (PING internetcoin.vip)
PRICE     tokenized MSFT, live
PYTHON    real Python 3.12 — loads ~10 MB once (type exit() to leave)
START     opens a program: START msn / pinball / doom / movies / games / payday
SYSTEMINFO what this machine is
TASKLIST  what's running
TIME      displays the time
TRACERT   traces the route to the payroll
TREE      folder tree
TYPE      prints a file (TYPE README.txt · TYPE ca.txt)
VER       Windows version
WHOAMI    you`;

function Cmd({ onClose, isFocus }) {
  const [lines, setLines] = useState(() => [
    { t: 'Microsoft Windows XP [Version 5.1.2600]' },
    { t: '(C) the internet 2025-2026. parody, not Microsoft. type HELP.' },
    { t: '' },
    { t: 'tip: PYTHON starts a real python shell · START doom starts doom · PAYDAY reads the chain', c: 'dim' },
    { t: '' },
  ]);
  const [input, setInput] = useState('');
  const [cwd, setCwd] = useState(HOME);
  const [mode, setMode] = useState('cmd'); // cmd | py | js
  const [pyPrompt, setPyPrompt] = useState('>>> ');
  const [busy, setBusy] = useState(false);
  const [colors, setColors] = useState({ bg: '#000', fg: '#c0c0c0' });
  const histRef = useRef({ list: [], i: -1 });
  const pyRef = useRef(null);
  const aliveRef = useRef(true);
  const bodyRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => () => { aliveRef.current = false; }, []);
  useEffect(() => {
    const b = bodyRef.current;
    if (b) b.scrollTop = b.scrollHeight;
  }, [lines, busy]);
  useEffect(() => {
    if (isFocus && inputRef.current) inputRef.current.focus({ preventScroll: true });
  }, [isFocus, busy, mode]);

  const print = useCallback((text, c) => {
    if (!aliveRef.current) return;
    const parts = String(text === undefined ? '' : text).replace(/\r/g, '').split('\n');
    setLines(ls => [...ls, ...parts.map(t => ({ t, c }))].slice(-2500));
  }, []);

  function focusInput() {
    const s = window.getSelection && window.getSelection();
    if (s && String(s).length) return; // let people copy output
    if (inputRef.current) inputRef.current.focus({ preventScroll: true });
  }

  // ---------- python (pyodide) ----------
  async function bootPython() {
    if (pyRef.current) return pyRef.current;
    print('loading python 3.12 (pyodide 0.27.2 from jsDelivr, ~10 MB, once per visit)…', 'dim');
    if (!window.loadPyodide) {
      await new Promise((res, rej) => {
        const s = document.createElement('script');
        s.src = `${PYODIDE_BASE}pyodide.js`;
        s.async = true;
        s.onload = res;
        s.onerror = () => rej(new Error('could not fetch pyodide.js — check your connection, fren'));
        document.head.appendChild(s);
      });
    }
    const py = await window.loadPyodide({ indexURL: PYODIDE_BASE });
    const ns = py.globals.get('dict')();
    py.runPython(
      `import builtins
from pyodide.ffi import to_js
from pyodide.console import PyodideConsole, repr_shorten, BANNER
import __main__
__main__.internet = {"ca": "${CA}", "site": "https://internetcoin.vip", "chain": 4663, "quote": "MSFT", "to_holders": "1.7% of every trade", "mission": "onboard the internet"}
pyconsole = PyodideConsole(__main__.__dict__)
async def await_fut(fut):
    res = await fut
    if res is not None:
        builtins._ = res
    return to_js([res], depth=1)
`,
      { globals: ns },
    );
    const pyconsole = ns.get('pyconsole');
    const awaitFut = ns.get('await_fut');
    const reprShorten = ns.get('repr_shorten');
    const banner = String(ns.get('BANNER'));
    pyconsole.stdout_callback = s => print(s.replace(/\n$/, ''));
    pyconsole.stderr_callback = s => print(s.replace(/\n$/, ''), 'err');
    pyRef.current = { py, pyconsole, awaitFut, reprShorten, banner };
    return pyRef.current;
  }
  async function pyLine(line) {
    const t = line.trim();
    if (t === 'exit' || t === 'exit()' || t === 'quit' || t === 'quit()') {
      setMode('cmd');
      setPyPrompt('>>> ');
      print('', undefined);
      print('(back to cmd.exe — python stays loaded, type PYTHON to resume)', 'dim');
      return;
    }
    const { pyconsole, awaitFut, reprShorten } = pyRef.current;
    const fut = pyconsole.push(line);
    const check = fut.syntax_check;
    if (check === 'incomplete') {
      setPyPrompt('... ');
      return;
    }
    setPyPrompt('>>> ');
    if (check === 'syntax-error') {
      print(String(fut.formatted_error || 'SyntaxError').trimEnd(), 'err');
      fut.destroy();
      return;
    }
    const wrapped = awaitFut(fut);
    setBusy(true);
    try {
      const [value] = await wrapped;
      if (value !== undefined) print(String(reprShorten.callKwargs(value, { separator: '\n<long output truncated>\n' })));
      if (value && typeof value.destroy === 'function') value.destroy();
    } catch (e) {
      const msg = (e && e.constructor && e.constructor.name === 'PythonError' && fut.formatted_error) || (e && e.message) || String(e);
      print(String(msg).trimEnd(), 'err');
    } finally {
      try { fut.destroy(); } catch (_) { /* already gone */ }
      try { wrapped.destroy(); } catch (_) { /* already gone */ }
      if (aliveRef.current) setBusy(false);
    }
  }
  async function startPython() {
    setBusy(true);
    try {
      const { banner } = await bootPython();
      print(banner);
      print('internet is a dict. try: internet["ca"]  ·  import this  ·  exit() to go back', 'dim');
      setMode('py');
      setPyPrompt('>>> ');
    } catch (e) {
      print(`python failed to load: ${e.message}`, 'err');
    } finally {
      if (aliveRef.current) setBusy(false);
    }
  }

  // ---------- js ----------
  function jsLine(line) {
    const t = line.trim();
    if (t === 'exit' || t === 'exit()' || t === 'quit') {
      setMode('cmd');
      print('(back to cmd.exe)', 'dim');
      return;
    }
    if (!t) return;
    try {
      // eslint-disable-next-line no-eval
      const v = (0, eval)(t);
      print(fmtJs(v));
    } catch (e) {
      print(`${e.name}: ${e.message}`, 'err');
    }
  }
  function fmtJs(v) {
    if (v === undefined) return 'undefined';
    if (typeof v === 'function') return String(v);
    if (typeof v === 'string') return JSON.stringify(v);
    try { return typeof v === 'object' ? JSON.stringify(v, null, 1).replace(/\n\s*/g, ' ') : String(v); } catch (_) { return String(v); }
  }

  // ---------- cmd ----------
  async function runCmd(raw) {
    const line = raw.trim();
    if (!line) return;
    const [c0, ...restArr] = line.split(/\s+/);
    const cmd = c0.toLowerCase();
    const rest = line.slice(c0.length).trim();
    const notFound = () => print(`'${c0}' is not recognized as an internal or external command,\noperable program or batch file.`);
    switch (cmd) {
      case 'help':
      case '/?':
        print(HELP);
        break;
      case 'cls':
      case 'clear':
        setLines([]);
        break;
      case 'exit':
        onClose();
        break;
      case 'ver':
        print('\nMicrosoft Windows XP [Version 5.1.2600]\nthe internet edition · est. 2025 · service pack 2');
        break;
      case 'echo':
        print(rest === '.' ? '' : rest.replace(/%username%/gi, 'fren').replace(/%computername%/gi, 'THE-INTERNET') || 'ECHO is on.');
        break;
      case 'date':
        print(`The current date is: ${new Date().toLocaleDateString('en-US', { weekday: 'short', month: '2-digit', day: '2-digit', year: 'numeric' })}`);
        break;
      case 'time':
        print(`The current time is: ${new Date().toLocaleTimeString('en-US', { hour12: false })}.${String(Date.now() % 100).padStart(2, '0')}`);
        break;
      case 'whoami':
        print('the-internet\\fren');
        break;
      case 'hostname':
        print('THE-INTERNET');
        break;
      case 'pwd':
        print(cwd);
        break;
      case 'cd':
      case 'chdir': {
        if (!rest) { print(cwd); break; }
        const p = resolve(cwd, rest);
        if (!p) print('The system cannot find the path specified.');
        else setCwd(p);
        break;
      }
      case 'dir':
      case 'ls': {
        if (cmd === 'ls') print("(that's linux, fren — here's DIR anyway)", 'dim');
        const all = /(^|\s)(\/a|-a|\/ah)(\s|$)/i.test(rest);
        const p = resolve(cwd, rest.replace(/(^|\s)(\/[a-z]+|-[a-z]+)(?=\s|$)/gi, '').trim());
        if (!p) print('File Not Found');
        else print(listing(p, all));
        break;
      }
      case 'attrib': {
        const node = FS[cwd.toLowerCase()];
        (node.hidden || []).forEach(d => print(`A  H     ${cwd}\\${d}`));
        node.files.forEach(([n]) => print(`A        ${cwd}\\${n}`));
        break;
      }
      case 'tree':
        print(['Folder PATH listing', 'Volume serial number is 8888-2E2C', 'C:.', ...tree()].join('\n'));
        break;
      case 'type':
      case 'cat': {
        const f = rest.toLowerCase().replace(/^"|"$/g, '');
        if (!f) { print('The syntax of the command is incorrect.'); break; }
        if (f.endsWith('readme.txt')) print(README);
        else if (f.endsWith('basement.nfo')) print(BASEMENT_NFO, 'lime');
        else if (f.endsWith('do_not_read.txt')) print('you read it. of course you did.\nthe key is not in this file. it never was. read the other one.');
        else if (f.endsWith('ca.txt')) {
          setBusy(true);
          try {
            const r = await fetch('/ca.txt', { cache: 'no-store' });
            print(r.ok ? await r.text() : `STATUS: LIVE\nCA: ${CA}`);
          } catch (_) {
            print(`CA: ${CA}`);
          } finally { setBusy(false); }
        } else if (f.endsWith('.exe') || f.endsWith('.dll') || f.endsWith('.lnk')) print('MZ\u0090\u0000\u0003\u0000\u0000\u0000\u0004\u0000\u0000\u0000\u00ff\u00ff  (binary, fren. use START.)');
        else print('The system cannot find the file specified.');
        break;
      }
      case 'ca':
        print(`\nCA: ${CA}\n\nthe only one. also at https://internetcoin.vip/ca.txt and in VERIFY.exe.\nanything else claiming to be Internet Coin is fake.`);
        break;
      case 'color': {
        const m = /^([0-9a-f])([0-9a-f])?$/i.exec(rest);
        if (!m) { print('Sets the default console foreground and background colors.\n\nCOLOR [attr]   attr = two hex digits, background then foreground. COLOR 0A = lime on black.'); break; }
        const bg = COLORS[m[1].toLowerCase()];
        const fg = m[2] ? COLORS[m[2].toLowerCase()] : '#c0c0c0';
        if (bg === fg) print('(same colour for text and background? bold. denied.)');
        else setColors({ bg, fg });
        break;
      }
      case 'ipconfig':
        print(`\nWindows IP Configuration\n\n\nEthernet adapter The Internet:\n\n        Connection-specific DNS Suffix  . : internetcoin.vip\n        IP Address. . . . . . . . . . . . : 88.88.88.88\n        Subnet Mask . . . . . . . . . . . : 255.255.255.0\n        Default Gateway . . . . . . . . . : rpc.mainnet.chain.robinhood.com\n        Chain ID. . . . . . . . . . . . . : 4663\n        Quote Asset . . . . . . . . . . . : tokenized MSFT`);
        break;
      case 'netstat':
        print(`\nActive Connections\n\n  Proto  Local Address          Foreign Address                             State\n  TCP    the-internet:msn       internet-coin-chat.team-27f.workers.dev:443  ESTABLISHED\n  TCP    the-internet:rpc       rpc.mainnet.chain.robinhood.com:443          ESTABLISHED\n  TCP    the-internet:1995      archive.org:443                              ESTABLISHED\n  TCP    the-internet:2003      web.archive.org:443                          TIME_WAIT\n  TCP    the-internet:payday    ${DISTRIBUTOR.slice(0, 10)}…:4663                       LISTENING`);
        break;
      case 'ping': {
        const h = restArr[0] ? restArr[0].replace(/^-t$/, 'internetcoin.vip') : '';
        if (!h) { print('\nUsage: ping target_name\n\ntry: PING internetcoin.vip'); break; }
        setBusy(true);
        if (/wall ?street|nasdaq|nyse/i.test(h)) {
          print(`\nPinging ${h} [0.0.0.0] with 32 bytes of data:\n`);
          for (let i = 0; i < 4; i++) { await sleep(700); print('Request timed out.'); }
          print(`\nPing statistics for 0.0.0.0:\n    Packets: Sent = 4, Received = 0, Lost = 4 (100% loss),\n\n(wall street closes at 4. the internet doesn't.)`);
        } else {
          print(`\nPinging ${h} [88.88.88.88] with 32 bytes of data:\n`);
          for (let i = 0; i < 4; i++) { await sleep(600); print('Reply from 88.88.88.88: bytes=32 time=88ms TTL=128'); }
          print('\nPing statistics for 88.88.88.88:\n    Packets: Sent = 4, Received = 4, Lost = 0 (0% loss),\nApproximate round trip times in milli-seconds:\n    Minimum = 88ms, Maximum = 88ms, Average = 88ms');
        }
        setBusy(false);
        break;
      }
      case 'tracert': {
        const h = restArr[0] || 'internetcoin.vip';
        setBusy(true);
        print(`\nTracing route to ${h} [88.88.88.88]\nover a maximum of 30 hops:\n`);
        const hops = [
          '  1     1 ms     1 ms     1 ms  your-router [192.168.1.1]',
          '  2    12 ms    11 ms    12 ms  your-isp',
          '  3    28 ms    27 ms    29 ms  the-internet',
          '  4    41 ms    40 ms    41 ms  robinhood-chain [4663]',
          `  5    58 ms    57 ms    58 ms  pons-distributor [${DISTRIBUTOR.slice(0, 6)}…${DISTRIBUTOR.slice(-4)}]`,
          '  6    88 ms    88 ms    88 ms  your-wallet (paid in tokenized MSFT)',
        ];
        for (const hop of hops) { await sleep(450); print(hop); }
        print('\nTrace complete.');
        setBusy(false);
        break;
      }
      case 'payday': {
        setBusy(true);
        print('reading the distributor…', 'dim');
        try {
          const l = await getLedger(true);
          const c = compute(l);
          print(`\nPAYROLL STATUS: ${l.live ? 'LIVE — read from the distributor just now' : 'cached (chain read failed, showing last known)'}\n  asset paid . . . . . . : tokenized ${c.asset}\n  epochs published . . . : ${c.epochs.length}\n  ${c.asset} paid to holders  : ${fmtAmt(c.paidTotal)}\n  pending (unallocated)  : ${fmtAmt(c.pendingAmount)}\n  distributor  . . . . . : ${DISTRIBUTOR}\n\nno volume, no payday. the pot rolls. (real) — your own number: PAYDAY.exe on the desktop.`);
        } catch (e) {
          print(`payroll read failed: ${e.message}`, 'err');
        } finally { setBusy(false); }
        break;
      }
      case 'price': {
        setBusy(true);
        print('asking the pool…', 'dim');
        try {
          const m = await fetchMsft();
          if (!m || typeof m.price !== 'number') print('NO SIGNAL');
          else print(`\nMSFT (tokenized, Robinhood Chain 4663)\n  price  . . . . : ${fmtUsd(m.price)}\n  24h  . . . . . : ${m.chg24 === null || m.chg24 === undefined ? '—' : `${m.chg24 > 0 ? '+' : ''}${Number(m.chg24).toFixed(2)}%`}\n  liquidity  . . : $${fmtCompact(m.liq)}\n  24h volume . . : $${fmtCompact(m.vol24)}\n\nwall street closes at 4pm. this doesn't.`);
        } catch (e) {
          print('NO SIGNAL', 'err');
        } finally { setBusy(false); }
        break;
      }
      case 'start': {
        const key = rest.toLowerCase().replace(/[^a-z0-9]/g, '');
        if (key === 'casino') {
          if (CASINO_LIVE || CASINO_PRACTICE) { window.dispatchEvent(new CustomEvent('ic:open', { detail: 'Casino' })); print('starting CASINO.exe…', 'dim'); }
          else print('CASINO.exe: the floor is not open yet. built, tested, not deployed.', 'dim');
          break;
        }
        if (!key) { print('START <program>   e.g. START msn · START pinball · START doom · START movies · START games · START payday'); break; }
        const app = START_MAP[key];
        if (app) { window.dispatchEvent(new CustomEvent('ic:open', { detail: app })); print(`starting ${app}…`, 'dim'); break; }
        const g = findGame(rest);
        if (g) { openGame(g); print(`starting ${g.title} (${g.year})…`, 'dim'); break; }
        print(`Windows cannot find '${rest}'. Make sure you typed the name correctly. (try START games)`);
        break;
      }
      case 'python':
      case 'python3':
      case 'py':
        await startPython();
        break;
      case 'js':
      case 'node':
        if (cmd === 'node') print('(no node here. you get the browser\'s own javascript, fren)', 'dim');
        setMode('js');
        print('JavaScript REPL — this tab\'s engine. type exit to leave.', 'dim');
        break;
      case 'java':
      case 'javac':
        print(`'${c0}' is not recognized as an internal or external command,\noperable program or batch file.\n\n(nobody installed java. nobody ever will. the internet runs on PYTHON now — type it.)`);
        break;
      case 'tasklist':
        print(`\nImage Name                     PID Session Name        Mem Usage\n========================= ======== ================ ============\nSystem Idle Process              0 Console               16 K\nexplorer.exe                   888 Console           18,880 K\nmsmsgs.exe                    2003 Console            8,888 K\nwinamp.exe                    1997 Console            4,201 K\npayday.exe                    1700 Console            1,700 K\nmission.exe                  55000 Console               55 K\niexplore.exe                  2001 Console           12,000 K\ncmd.exe                         88 Console            2,048 K\ndo_not_open.exe                666 Console          666,666 K`);
        break;
      case 'systeminfo':
        print(`\nHost Name:                 THE-INTERNET\nOS Name:                   Microsoft Windows XP (the internet edition — parody)\nOS Version:                5.1.2600 Service Pack 2 Build 2600\nRegistered Owner:          fren\nOriginal Install Date:     9/5/2026, 7:59:00 PM\nSystem Boot Time:          ${new Date().toLocaleString('en-US')}\nTotal Physical Memory:     888 MB\nChain:                     Robinhood Chain (4663)\nQuote Asset:               tokenized MSFT\nTo Holders:                1.7% of every trade, airdropped by pons every epoch\nContract:                  ${CA}\nMission:                   onboard the internet — 55,000,000 frens`);
        break;
      case 'format':
        print('The type of the file system is NTFS.\nWARNING, ALL DATA ON NON-REMOVABLE DISK\nDRIVE C: WILL BE LOST!\nProceed with Format (Y/N)? n\n\nwe hold.');
        break;
      case 'del':
      case 'erase':
      case 'rmdir':
      case 'rd':
      case 'rm':
        print('Access is denied.\n(paper hands are not installed on this machine.)');
        break;
      case 'mkdir':
      case 'md':
      case 'copy':
      case 'move':
      case 'ren':
        print('Access is denied.\n(this drive is read-only. we hold.)');
        break;
      case 'shutdown':
      case 'logoff':
        print('The internet does not turn off.');
        break;
      case 'sudo':
        print('sudo? this is windows, fren.');
        break;
      case 'git':
        print("fatal: not a git repository (we don't push. we hold.)");
        break;
      case 'nudge':
        print('use Windows Messenger → Nudge. everyone\'s window shakes. (real)');
        break;
      case 'matrix': {
        setBusy(true);
        for (let i = 0; i < 18; i++) {
          await sleep(60);
          print(Array.from({ length: 64 }, () => (Math.random() < 0.5 ? '0' : Math.random() < 0.9 ? '1' : '8')).join(''), 'lime');
        }
        print('wake up, fren. the internet has you.', 'lime');
        setBusy(false);
        break;
      }
      case 'title':
        print('(window titles are set at open. cmd.exe it stays.)', 'dim');
        break;
      case 'basement':
        if (isUnlocked()) { window.dispatchEvent(new CustomEvent('ic:open', { detail: 'Basement' })); print('starting BASEMENT.exe…', 'dim'); }
        else print(`'basement' is not recognized as an internal or external command,\noperable program or batch file.\n\n(not yet, fren. the door is on the internal web.)`);
        break;
      default:
        notFound();
    }
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (busy) return;
    const raw = input;
    setInput('');
    if (raw.trim()) {
      const h = histRef.current;
      h.list.push(raw);
      h.i = h.list.length;
    }
    const ps = mode === 'py' ? pyPrompt : mode === 'js' ? 'js> ' : `${cwd}>`;
    print(`${ps}${raw}`, 'in');
    if (mode === 'py') await pyLine(raw);
    else if (mode === 'js') jsLine(raw);
    else {
      await runCmd(raw);
      if (mode === 'cmd' && aliveRef.current) print('');
    }
  }
  function onKeyDown(e) {
    const h = histRef.current;
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!h.list.length) return;
      h.i = Math.max(0, h.i - 1);
      setInput(h.list[h.i] || '');
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      h.i = Math.min(h.list.length, h.i + 1);
      setInput(h.list[h.i] || '');
    } else if (e.key === 'c' && e.ctrlKey) {
      if (mode === 'py') { print('KeyboardInterrupt', 'err'); setPyPrompt('>>> '); if (pyRef.current) try { pyRef.current.pyconsole.buffer.clear && pyRef.current.pyconsole.buffer.clear(); } catch (_) { /* fine */ } }
      setInput('');
    } else if (e.key === 'Tab') {
      e.preventDefault();
    }
  }

  const ps = mode === 'py' ? pyPrompt : mode === 'js' ? 'js> ' : `${cwd}>`;
  return (
    <Div bg={colors.bg} fg={colors.fg} onMouseDown={focusInput}>
      <div className="cmd__body" ref={bodyRef}>
        {lines.map((l, i) => (
          <div key={i} className={`cmd__line${l.c ? ` cmd__line--${l.c}` : ''}`}>{l.t || '\u00a0'}</div>
        ))}
        {busy ? (
          <div className="cmd__line cmd__line--dim">_</div>
        ) : (
          <form className="cmd__prompt" onSubmit={onSubmit}>
            <span className="cmd__ps">{ps}</span>
            <input
              ref={inputRef}
              className="cmd__input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              aria-label="command input"
            />
          </form>
        )}
      </div>
    </Div>
  );
}

const Div = styled.div`
  height: 100%;
  width: 100%;
  position: absolute;
  background: ${({ bg }) => bg};
  color: ${({ fg }) => fg};
  font-family: 'Lucida Console', Consolas, 'Courier New', monospace;
  font-size: 12px;
  line-height: 14px;
  overflow: hidden;
  cursor: text;
  .cmd__body { position: absolute; inset: 0; overflow-y: auto; padding: 2px 4px 6px; }
  .cmd__line { white-space: pre-wrap; word-break: break-all; min-height: 14px; }
  .cmd__line--dim { opacity: .6; }
  .cmd__line--err { color: #ff6b6b; }
  .cmd__line--lime { color: #ccff00; }
  .cmd__prompt { display: flex; align-items: baseline; }
  .cmd__ps { white-space: pre; flex-shrink: 0; }
  .cmd__input { flex: 1; min-width: 40px; background: transparent; border: 0; outline: 0; padding: 0; margin: 0; color: inherit; font: inherit; caret-color: currentColor; }
  ::selection { background: #c0c0c0; color: #000; }
`;

export default Cmd;
